import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUser, getToken } from './lib/auth';
import { Toaster } from 'sonner';
import { setupNotificationListener } from './lib/pushNotifications';
import { initializeNativePush, isNativeApp } from './lib/nativePush';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import api from './lib/api';
import { silentRefresh } from './lib/session';
import NewCheckoutWatcher from './components/NewCheckoutWatcher';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyResultPage from './pages/VerifyResultPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CheckoutPage from './pages/CheckoutPage';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import LogsPage from './pages/LogsPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import PricingPage from './pages/PricingPage';
import BillingPage from './pages/BillingPage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import GuidePage from './pages/GuidePage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PerformancePage from './pages/PerformancePage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import ManagerPerformancePage from './pages/ManagerPerformancePage';
import AuthCallback from './pages/AuthCallback';
import OwnerDashboardPage from './pages/OwnerDashboardPage';

function ProtectedRoute({ children, allowExpired = false }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        setAuthenticated(false);
        setLoading(false);
        return;
      }
      
      setAuthenticated(true);
      
      // Check subscription status
      try {
        const response = await api.get('/subscription');
        const sub = response.data;
        
        // Check if trial has expired or subscription is inactive
        if (sub.subscription_status === 'expired' || 
            (sub.subscription_status === 'trialing' && sub.trial_end_date && new Date(sub.trial_end_date) < new Date())) {
          setTrialExpired(true);
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to trial expired page if trial is over (unless allowExpired is true)
  if (trialExpired && !allowExpired) {
    return <Navigate to="/trial-expired" replace />;
  }

  return children;
}

function AdminRoute({ children }) {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const user = getUser();
    setAuthorized(isAuthenticated() && user?.role === 'admin');
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!authorized) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const reset = () => {
      const app = document.querySelector('.App');
      if (app) app.scrollTop = 0;
      try { window.scrollTo(0, 0); } catch (e) { /* noop */ }
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      document.querySelectorAll('.overflow-y-auto, [data-scroll-root], main').forEach((el) => { el.scrollTop = 0; });
    };
    reset();
    const raf = requestAnimationFrame(reset);
    const t = setTimeout(reset, 60);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    // Initialize app based on platform
    const initApp = async () => {
      if (Capacitor.isNativePlatform()) {
        // Native app initialization
        try {
          // Hide splash screen
          await SplashScreen.hide();
          
          // Set status bar style
          // Status bar: iOS overlays the web view (light app bg shows behind it, auth screens
          // bleed full-screen). Android keeps a solid light status bar (env() insets are
          // unreliable on Android), so content stays below it.
          if (Capacitor.getPlatform() === 'ios') {
            await StatusBar.setStyle({ style: Style.Light });
            await StatusBar.setOverlaysWebView({ overlay: true });
          } else if (Capacitor.getPlatform() === 'android') {
            await StatusBar.setStyle({ style: Style.Light });
            await StatusBar.setOverlaysWebView({ overlay: false });
            await StatusBar.setBackgroundColor({ color: '#EFF6FF' });
          }
          
          // Initialize native push notifications if user is authenticated
          if (isAuthenticated()) {
            await initializeNativePush(async (fcmToken) => {
              // Register token with backend
              try {
                await api.post('/native/devices', {
                  fcmToken,
                  platform: Capacitor.getPlatform()
                });
                console.log('Native device registered with backend');
              } catch (err) {
                console.error('Failed to register native device:', err);
              }
            });
          }
        } catch (error) {
          console.error('Native app initialization error:', error);
        }
      } else {
        // Web app - use web push
        setupNotificationListener();
      }
    };
    
    initApp();
  }, []);

  // Keep the session alive: slide the token whenever the app opens/regains focus.
  useEffect(() => {
    silentRefresh();
    const onFocus = () => silentRefresh();
    const onVisibility = () => { if (document.visibilityState === 'visible') silentRefresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <NewCheckoutWatcher />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-result" element={<VerifyResultPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/c/:slug" element={<CheckoutPage />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/properties/:id"
            element={
              <ProtectedRoute>
                <PropertyDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkouts"
            element={
              <ProtectedRoute>
                <LogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logs"
            element={
              <ProtectedRoute>
                <LogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/performance"
            element={
              <ProtectedRoute>
                <TeamPerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/performance/:staffId"
            element={
              <ProtectedRoute>
                <PerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/performance"
            element={
              <ProtectedRoute>
                <ManagerPerformancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowExpired>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/owner"
            element={
              <ProtectedRoute allowExpired>
                <OwnerDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute allowExpired>
                <PricingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowExpired>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trial-expired"
            element={
              <ProtectedRoute allowExpired>
                <TrialExpiredPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide"
            element={
              <ProtectedRoute>
                <GuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guide/staff"
            element={
              <ProtectedRoute>
                <GuidePage />
              </ProtectedRoute>
            }
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-right"
        richColors
        offset={{ top: '24px' }}
        mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', left: '12px', right: '12px' }}
      />
    </div>
  );
}

export default App;
